/**
 * Custom hook for managing installment transactions
 *
 * Provides:
 * - Fetching installment series (parent transactions with total_installments > 1)
 * - Grouping child transactions by parent
 * - Statistics calculation (paid count, remaining, progress percentage)
 * - Filtering and search capabilities
 */

import { useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Transaction } from '@/types/transaction';
import { fromDateString } from '@/utils/dateUtils';

// Types for installment transaction operations
export interface Installment {
  id: string;
  current_installment: number;
  amount: number;
  date: Date | string;
  due_date?: Date | string | null;
  payment_date?: Date | string | null;
  is_paid: boolean;
  status: 'PENDING' | 'PAID' | 'OVERDUE' | 'CANCELLED';
  description: string;
}

export interface InstallmentSeries {
  id: string;
  description: string;
  amount: number; // Total amount of the series
  total_installments: number;
  installment_amount: number; // Amount per installment
  category: string;
  category_id?: number;
  type: 'income' | 'expense';
  status: 'active' | 'completed' | 'cancelled';
  installments: Installment[];
  paid_count: number;
  remaining_count: number;
  progress_percentage: number;
  next_due_date?: Date | string | null;
  date: Date | string; // Start date of first installment
}

export interface InstallmentFilters {
  search?: string;
  status?: 'all' | 'active' | 'completed' | 'overdue';
  category_id?: number;
  type?: 'all' | 'income' | 'expense';
}

const STALE_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * Calculate installment status based on dates and payment status
 */
const getInstallmentStatus = (
  isPaid: boolean,
  dueDate?: Date | string | null,
  paymentDate?: Date | string | null
): Installment['status'] => {
  if (isPaid || paymentDate) return 'PAID';

  if (dueDate) {
    const due = dueDate instanceof Date ? dueDate : new Date(dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);

    if (due < today) return 'OVERDUE';
  }

  return 'PENDING';
};

/**
 * Transform raw transaction data into InstallmentSeries
 */
const transformToInstallmentSeries = (
  parent: Transaction,
  children: Transaction[]
): InstallmentSeries => {
  const installments: Installment[] = children.map((child) => ({
    id: child.id,
    current_installment: child.current_installment || 1,
    amount: child.amount,
    date: child.date,
    due_date: child.due_date,
    payment_date: child.payment_date,
    is_paid: child.is_paid || !!child.payment_date,
    status: getInstallmentStatus(child.is_paid || false, child.due_date, child.payment_date),
    description: child.description || parent.description,
  }));

  // Sort installments by current_installment
  installments.sort((a, b) => a.current_installment - b.current_installment);

  const paidCount = installments.filter((i) => i.is_paid).length;
  const remainingCount = installments.length - paidCount;
  const progressPercentage = installments.length > 0
    ? (paidCount / installments.length) * 100
    : 0;

  // Find next unpaid installment due date
  const nextUnpaid = installments.find((i) => !i.is_paid);
  const nextDueDate = nextUnpaid?.due_date || null;

  // Determine series status
  let status: InstallmentSeries['status'] = 'active';
  if (remainingCount === 0) {
    status = 'completed';
  } else if (installments.some((i) => i.status === 'CANCELLED')) {
    status = 'cancelled';
  }

  return {
    id: parent.id,
    description: parent.description,
    amount: parent.amount,
    total_installments: parent.total_installments || installments.length,
    installment_amount: parent.amount / (parent.total_installments || installments.length),
    category: (parent.categories as any)?.name || parent.category || 'Sem Categoria',
    category_id: parent.category_id,
    type: parent.type,
    status,
    installments,
    paid_count: paidCount,
    remaining_count: remainingCount,
    progress_percentage: progressPercentage,
    next_due_date: nextDueDate,
    date: parent.date,
  };
};

/**
 * Calculate statistics for all installment series
 */
export const calculateInstallmentStatistics = (series: InstallmentSeries[]) => {
  const activeSeries = series.filter((s) => s.status === 'active');
  const completedSeries = series.filter((s) => s.status === 'completed');

  const totalValue = series.reduce((sum, s) => sum + s.amount, 0);
  const paidValue = series.reduce(
    (sum, s) => sum + s.installment_amount * s.paid_count,
    0
  );
  const remainingValue = totalValue - paidValue;

  const overallProgress = totalValue > 0 ? (paidValue / totalValue) * 100 : 0;

  return {
    total_series: series.length,
    active_series: activeSeries.length,
    completed_series: completedSeries.length,
    total_value: totalValue,
    paid_value: paidValue,
    remaining_value: remainingValue,
    overall_progress: overallProgress,
  };
};

export function useInstallments(filters?: InstallmentFilters) {
  const queryClient = useQueryClient();

  // Fetch installment series with filters
  const { data: installmentSeries = [], isLoading, error } = useQuery({
    queryKey: ['installment-series', filters],
    queryFn: async () => {
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Query 1: Get all parent transactions with installments
      let parentQuery = supabase
        .from('transactions')
        .select(`
          *,
          categories (
            id,
            name
          ),
          clients:client_id (
            id,
            name
          ),
          suppliers:supplier_id (
            id,
            name
          )
        `)
        .eq('user_id', user.id)
        .gt('total_installments', 1)
        .is('parent_transaction_id', null);

      // Apply type filter
      if (filters?.type && filters.type !== 'all') {
        parentQuery = parentQuery.eq('type', filters.type);
      }

      const { data: parents, error: parentsError } = await parentQuery.order('created_at', { ascending: false });

      if (parentsError) {
        throw parentsError;
      }

      if (!parents || parents.length === 0) {
        return [];
      }

      // Query 2: Get all child transactions for these parents
      const parentIds = parents.map((p) => p.id);
      const { data: children, error: childrenError } = await supabase
        .from('transactions')
        .select('*')
        .in('parent_transaction_id', parentIds)
        .order('current_installment', { ascending: true });

      if (childrenError) {
        throw childrenError;
      }

      // Group children by parent
      const childrenByParent = new Map<string, Transaction[]>();
      for (const child of children || []) {
        if (child.parent_transaction_id) {
          if (!childrenByParent.has(child.parent_transaction_id)) {
            childrenByParent.set(child.parent_transaction_id, []);
          }
          childrenByParent.get(child.parent_transaction_id)!.push(child);
        }
      }

      // Transform to InstallmentSeries
      const series: InstallmentSeries[] = parents.map((parent) => {
        const parentChildren = childrenByParent.get(parent.id) || [];
        return transformToInstallmentSeries(parent as Transaction, parentChildren);
      });

      // Apply client-side filters
      let filteredSeries = series;

      // Search filter
      if (filters?.search) {
        const searchLower = filters.search.toLowerCase();
        filteredSeries = filteredSeries.filter((s) =>
          s.description.toLowerCase().includes(searchLower)
        );
      }

      // Category filter
      if (filters?.category_id) {
        filteredSeries = filteredSeries.filter((s) => s.category_id === filters.category_id);
      }

      // Status filter
      if (filters?.status && filters.status !== 'all') {
        filteredSeries = filteredSeries.filter((s) => {
          if (filters.status === 'active') return s.status === 'active';
          if (filters.status === 'completed') return s.status === 'completed';
          if (filters.status === 'overdue') {
            return s.installments.some((i) => i.status === 'OVERDUE' && !i.is_paid);
          }
          return true;
        });
      }

      return filteredSeries;
    },
    staleTime: 0, // Real-time updates - always refetch when invalidated
    refetchOnWindowFocus: true,
  });

  // Calculate statistics
  const statistics = useCallback(() => {
    return calculateInstallmentStatistics(installmentSeries);
  }, [installmentSeries]);

  // Real-time subscription for transaction changes
  useEffect(() => {
    const channel = supabase
      .channel(`installment-transactions-changes_${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'transactions',
        },
        (payload) => {
          // Invalidate and force refetch when transactions change
          // Only refetch if the change affects installments
          const { new: newRecord, old: oldRecord } = payload;

          // Check if the transaction is part of an installment series
          const isInstallmentRelated = () => {
            if (newRecord && (newRecord.total_installments > 1 || newRecord.parent_transaction_id)) {
              return true;
            }
            if (oldRecord && (oldRecord.total_installments > 1 || oldRecord.parent_transaction_id)) {
              return true;
            }
            return false;
          };

          if (isInstallmentRelated()) {
            // Force immediate refetch with refetchType: 'all'
            queryClient.invalidateQueries({
              queryKey: ['installment-series'],
              refetchType: 'all',
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  return {
    // Data
    installmentSeries,
    isLoading,
    error,

    // Computed
    statistics: statistics(),

    // States
    isFetching: isLoading,
  };
}

export type { InstallmentSeries, Installment };
